import { useCallback, useEffect, useMemo, useState } from "react";
import type { Reminder } from "@/lib/humhub-api/types";

interface UseReminderEmailDialogStateParams {
  reminders: Reminder[];
  initialScope?: "reminder" | "entry";
  initialReminderId?: number | null;
}

export const useReminderEmailDialogState = ({
  reminders,
  initialScope,
  initialReminderId = null,
}: UseReminderEmailDialogStateParams) => {
  const reminderOptions = useMemo(
    () => reminders.filter((reminder) => reminder.id != null),
    [reminders],
  );
  const hasReminders = reminderOptions.length > 0;
  const [emailScope, setEmailScope] = useState<"reminder" | "entry">(
    initialScope ?? (hasReminders ? "reminder" : "entry"),
  );
  const [selectedReminderId, setSelectedReminderId] = useState<number | null>(
    initialReminderId ?? null,
  );

  const selectFallbackReminder = useCallback(() => {
    const fallback = reminderOptions.find((reminder) => reminder.id != null);
    setSelectedReminderId(fallback?.id ?? null);
    return fallback?.id ?? null;
  }, [reminderOptions]);

  useEffect(() => {
    if (!hasReminders) {
      setSelectedReminderId(null);
      setEmailScope("entry");
      return;
    }

    if (emailScope === "entry") return;

    const fallbackReminder = reminderOptions.find(
      (reminder) => reminder.id != null,
    );
    if (selectedReminderId != null) {
      const exists = reminderOptions.some(
        (reminder) => reminder.id === selectedReminderId,
      );
      if (!exists) {
        const next = fallbackReminder ?? reminderOptions[0];
        setSelectedReminderId(next.id ?? null);
      }
      return;
    }

    if (fallbackReminder?.id != null) {
      setSelectedReminderId(fallbackReminder.id);
    }
  }, [emailScope, hasReminders, reminderOptions, selectedReminderId]);

  useEffect(() => {
    const scopeIsReminder = emailScope === "reminder";
    const selectionIsValid =
      selectedReminderId == null ||
      reminderOptions.some((reminder) => reminder.id === selectedReminderId);

    if (!hasReminders) {
      if (emailScope !== "entry") setEmailScope("entry");
      if (selectedReminderId !== null) setSelectedReminderId(null);
      return;
    }

    if (!scopeIsReminder) return;

    if (!selectionIsValid) selectFallbackReminder();
  }, [
    emailScope,
    hasReminders,
    reminderOptions,
    selectedReminderId,
    selectFallbackReminder,
  ]);

  const initializeForOpen = () => {
    if (hasReminders) {
      const nextId = selectFallbackReminder();
      setEmailScope("reminder");
      return nextId;
    }
    setSelectedReminderId(null);
    setEmailScope("entry");
    return null;
  };

  const selectedReminder = useMemo(() => {
    if (!hasReminders) return null;
    return (
      reminderOptions.find((reminder) => reminder.id === selectedReminderId) ??
      reminderOptions[0] ??
      null
    );
  }, [hasReminders, reminderOptions, selectedReminderId]);

  const reminderSelectValue =
    emailScope === "reminder"
      ? (selectedReminderId?.toString() ?? "entry")
      : "entry";

  const handleReminderSelect = (value: string) => {
    if (value === "entry") {
      setEmailScope("entry");
      setSelectedReminderId(null);
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      setEmailScope("reminder");
      setSelectedReminderId(parsed);
    }
  };

  return {
    emailScope,
    setEmailScope,
    selectedReminderId,
    setSelectedReminderId,
    selectedReminder,
    reminderOptions,
    hasReminders,
    initializeForOpen,
    reminderSelectValue,
    handleReminderSelect,
  };
};
